import { useState } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { SelectedFacets } from '@/api/searchService';

export type { SelectedFacets };

// ─── Types ────────────────────────────────────────────────────────────────────

type Props = {
  facetDistribution: {
    categoryIds?: Record<string, number>;
    hasFacebook?: Record<string, number>;
    hasInstagram?: Record<string, number>;
    hasWebsite?: Record<string, number>;
  };
  categoryIdToName: Map<string, string>;
  selectedFacets: SelectedFacets;
  onFacetChange: (facets: SelectedFacets) => void;
  onClearAll: () => void;
  // When true (locked-category pages), the category facet group is hidden and
  // categoryIds are excluded from the active-filter count (not user-clearable).
  hideCategoryFacet?: boolean;
};

const CATEGORY_CAP = 10; // show top N categories, rest behind "show more"

// Platform presence filters — value is display label; facetKey is the boolean on SelectedFacets.
const PLATFORM_FACETS: { key: 'hasFacebook' | 'hasInstagram' | 'hasWebsite'; label: string }[] = [
  { key: 'hasFacebook', label: 'فيسبوك' },
  { key: 'hasInstagram', label: 'انستجرام' },
  { key: 'hasWebsite', label: 'موقع إلكتروني' },
];

// Min-rating chips. Selecting a chip sets minRating; selecting the active chip clears it.
const RATING_OPTIONS = [4, 3, 2];

// ─── Helper ───────────────────────────────────────────────────────────────────

const toggle = (arr: string[], value: string): string[] =>
  arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value];

// ─── Component ────────────────────────────────────────────────────────────────

export default function FacetSidebar({
  facetDistribution,
  categoryIdToName,
  selectedFacets,
  onFacetChange,
  onClearAll,
  hideCategoryFacet,
}: Props) {
  const [showAllCategories, setShowAllCategories] = useState(false);

  const totalActive =
    (hideCategoryFacet ? 0 : selectedFacets.categoryIds.length) +
    (selectedFacets.hasFacebook ? 1 : 0) +
    (selectedFacets.hasInstagram ? 1 : 0) +
    (selectedFacets.hasWebsite ? 1 : 0) +
    (selectedFacets.minRating > 0 ? 1 : 0);

  // Category group — needs ObjectId for selection, Arabic name for display.
  // Sorted by count desc and capped to CATEGORY_CAP with a show-more toggle.
  const renderCategoryGroup = () => {
    const allEntries = Object.entries(facetDistribution.categoryIds ?? {})
      .filter(([, count]) => count > 0)
      .sort(([, a], [, b]) => b - a);
    if (allEntries.length === 0) return null;

    const entries = showAllCategories ? allEntries : allEntries.slice(0, CATEGORY_CAP);
    const hiddenCount = allEntries.length - CATEGORY_CAP;

    return (
      <div className="mb-5">
        <h3 className="text-sm font-semibold text-gray-700 mb-2 font-arabic">الفئة</h3>
        <div className="space-y-2">
          {entries.map(([id, count]) => {
            const name = categoryIdToName.get(id) ?? id;
            return (
              <div key={id} className="flex items-center gap-2">
                <Checkbox
                  id={`facet-cat-${id}`}
                  checked={selectedFacets.categoryIds.includes(id)}
                  onCheckedChange={() =>
                    onFacetChange({
                      ...selectedFacets,
                      categoryIds: toggle(selectedFacets.categoryIds, id),
                    })
                  }
                  className="data-[state=checked]:bg-warmOrange-500 data-[state=checked]:border-warmOrange-500"
                />
                <label
                  htmlFor={`facet-cat-${id}`}
                  className="flex-1 text-sm font-arabic cursor-pointer"
                >
                  {name}
                  <span className="text-xs text-muted-foreground ms-1">({count})</span>
                </label>
              </div>
            );
          })}
        </div>
        {hiddenCount > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowAllCategories((v) => !v)}
            className="mt-2 text-sm text-warmOrange-500 hover:text-warmOrange-600 font-arabic p-0 h-auto"
          >
            {showAllCategories ? 'عرض أقل' : `عرض المزيد (${hiddenCount})`}
          </Button>
        )}
      </div>
    );
  };

  // Platform presence group — each facet is a single boolean toggle.
  const renderPlatformGroup = () => (
    <div className="mb-5">
      <h3 className="text-sm font-semibold text-gray-700 mb-2 font-arabic">المنصات</h3>
      <div className="space-y-2">
        {PLATFORM_FACETS.map(({ key, label }) => {
          const count = facetDistribution[key]?.['true'] ?? 0;
          return (
            <div key={key} className="flex items-center gap-2">
              <Checkbox
                id={`facet-${key}`}
                checked={selectedFacets[key]}
                onCheckedChange={(checked) =>
                  onFacetChange({ ...selectedFacets, [key]: checked === true })
                }
                className="data-[state=checked]:bg-warmOrange-500 data-[state=checked]:border-warmOrange-500"
              />
              <label
                htmlFor={`facet-${key}`}
                className="flex-1 text-sm font-arabic cursor-pointer"
              >
                {label}
                <span className="text-xs text-muted-foreground ms-1">({count})</span>
              </label>
            </div>
          );
        })}
      </div>
    </div>
  );

  // Rating group — min-rating chips (4★+, 3★+, 2★+).
  const renderRatingGroup = () => (
    <div className="mb-5">
      <h3 className="text-sm font-semibold text-gray-700 mb-2 font-arabic">التقييم</h3>
      <div className="flex flex-wrap gap-2">
        {RATING_OPTIONS.map((value) => {
          const active = selectedFacets.minRating === value;
          return (
            <button
              key={value}
              type="button"
              aria-pressed={active}
              onClick={() =>
                onFacetChange({ ...selectedFacets, minRating: active ? 0 : value })
              }
              className={cn(
                'inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-sm font-arabic transition-colors',
                active
                  ? 'bg-warmOrange-500 border-warmOrange-500 text-white'
                  : 'bg-white border-border text-gray-700 hover:border-warmOrange-400'
              )}
            >
              {value}
              <span aria-hidden>★</span>
              <span>فأعلى</span>
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div dir="rtl" className="bg-warmBeige-50 rounded-xl p-6">
      {/* Header with title (right) and clear-all */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-base font-semibold text-gray-900 font-arabic text-right">
          تصفية النتائج
        </h2>
        {totalActive > 0 && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClearAll}
            className="text-sm text-warmOrange-500 hover:text-warmOrange-600 font-arabic p-0 h-auto"
          >
            مسح الكل
          </Button>
        )}
      </div>

      {/* Divider */}
      <div className="border-t border-border mb-4" />

      {/* Rating facets */}
      {renderRatingGroup()}

      {/* Platform presence facets */}
      {renderPlatformGroup()}

      {/* Category facets — hidden on locked-category pages */}
      {!hideCategoryFacet && renderCategoryGroup()}
    </div>
  );
}
